import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar, TrendingUp, Phone, Clock, BarChart3, MapPin, CheckCircle, PhoneCall, Users, Layers, Building2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const OverallReports = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [sections, setSections] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [selectedSection, setSelectedSection] = useState('all');
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState('all');
  
  useEffect(() => {
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setToDate(today.toISOString().split('T')[0]);
    
    fetchStaffList();
    fetchSections();
    fetchVenues();
    fetchBenchmarks();
    fetchAnalytics(thirtyDaysAgo.toISOString().split('T')[0], today.toISOString().split('T')[0], 'all', 'all', 'all');
  }, []);

  const fetchBenchmarks = async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      const response = await axios.get(`${BACKEND_URL}/api/industry-benchmarks/${propertyId}`);
      if (response.data.success && response.data.benchmark) {
        setBenchmarks(response.data.benchmark);
      }
    } catch (error) {
      console.error('Error fetching benchmarks:', error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      // Fetch staff with Pool and Beach Attendant and Server roles
      const [attendantsRes, serversRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/staff/by-role/${propertyId}/Pool And Beach Attendant`),
        axios.get(`${BACKEND_URL}/api/staff/by-role/${propertyId}/Pool and Beach Server`)
      ]);

      const attendants = attendantsRes.data.success ? attendantsRes.data.staff.map(s => ({...s, role: 'Attendant'})) : [];
      const servers = serversRes.data.success ? serversRes.data.staff.map(s => ({...s, role: 'Server'})) : [];
      
      setStaffList([...attendants, ...servers]);
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      const response = await axios.get(`${BACKEND_URL}/api/sections/${propertyId}`);
      if (response.data.success) {
        setSections(response.data.sections);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchVenues = async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      const response = await axios.get(`${BACKEND_URL}/api/venues/${propertyId}`);
      if (response.data.success) {
        setVenues(response.data.venues);
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
    }
  };

  const fetchAnalytics = async (from = fromDate, to = toDate, staffId = selectedStaff, sectionId = selectedSection, venueId = selectedVenue) => {
    try {
      setLoading(true);
      const userData = localStorage.getItem('userData');
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      const params = {
        from_date: from,
        to_date: to
      };
      
      if (staffId && staffId !== 'all') {
        params.staff_id = staffId;
      }
      
      if (sectionId && sectionId !== 'all') {
        params.section_id = sectionId;
      }
      
      if (venueId && venueId !== 'all') {
        params.venue_id = venueId;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/analytics/overall-reports/${propertyId}`,
        { params }
      );

      if (response.data.success) {
        setAnalytics(response.data.analytics);
        console.log('Analytics data:', response.data.analytics);
        console.log('Debug info:', response.data.debug);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (fromDate && toDate) {
      fetchAnalytics(fromDate, toDate, selectedStaff, selectedSection, selectedVenue);
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading analytics...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Overall Reports</h1>
            <p className="text-slate-600 mt-1">Analytics and insights for your property</p>
          </div>
        </div>

        {/* Date Filter */}
        <Card className="p-6">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Venue
              </label>
              <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Venues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {venues.map((venue) => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Section
              </label>
              <Select value={selectedSection} onValueChange={setSelectedSection}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Sections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sections</SelectItem>
                  {sections.map((section) => (
                    <SelectItem key={section.id} value={section.id}>
                      {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Staff
              </label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.name} ({staff.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleApplyFilter}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Apply Filter
            </Button>
          </div>
        </Card>

        {/* No Data Message */}
        {analytics && analytics.totalAllocations === 0 && (
          <Card className="p-12">
            <div className="text-center">
              <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Allocation Data Found</h3>
              <p className="text-slate-600 mb-4">
                There are no allocations in the selected date range.
              </p>
              <p className="text-sm text-slate-500">
                Try adjusting your date filters or create some allocations to see analytics here.
              </p>
            </div>
          </Card>
        )}

        {/* Analytics Cards */}
        {analytics && analytics.totalAllocations > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Total Allocations */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Allocations</p>
                    <h3 className="text-3xl font-bold text-slate-900">
                      {analytics.totalAllocations}
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </Card>

              {/* Average Allocation Length */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Avg Allocation Length</p>
                    <h3 className="text-3xl font-bold text-slate-900">
                      {analytics.avgAllocationLength}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">minutes</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </Card>

              {/* Total Calls */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Calls</p>
                    <h3 className="text-3xl font-bold text-slate-900">
                      {analytics.totalCalls}
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </Card>

              {/* Average Calls Per Allocation */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Avg Calls Per Allocation</p>
                    <h3 className="text-3xl font-bold text-slate-900">
                      {analytics.avgCallsPerAllocation}
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </Card>

              {/* Average Time Per Call */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Avg Time Per Call</p>
                    <h3 className="text-3xl font-bold text-slate-900">
                      {formatTime(analytics.avgTimePerCall)}
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
              </Card>

              {/* Proactive Checkins */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Proactive Checkins</p>
                    <h3 className="text-3xl font-bold text-green-600">
                      {analytics.proactiveCheckins || 0}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">Under 5 seconds</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </Card>

              {/* Avg Proactive Per Allocation */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Avg Proactive Per Allocation</p>
                    <h3 className="text-3xl font-bold text-green-600">
                      {analytics.avgProactivePerAllocation || 0}
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </Card>

              {/* On Demand Checkins */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">On Demand Checkins</p>
                    <h3 className="text-3xl font-bold text-amber-600">
                      {analytics.onDemandCheckins || 0}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">5 seconds or more</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <PhoneCall className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </Card>

              {/* Avg On Demand Per Allocation */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Avg On Demand Per Allocation</p>
                    <h3 className="text-3xl font-bold text-amber-600">
                      {analytics.avgOnDemandPerAllocation || 0}
                    </h3>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <PhoneCall className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Day-wise Allocations Graph */}
            <Card className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-800 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                  Allocations Over Time
                </h2>
                <p className="text-sm text-slate-600 mt-1">Day-wise allocation trends</p>
              </div>

              {analytics.dayWiseAllocations && analytics.dayWiseAllocations.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={analytics.dayWiseAllocations}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="count" 
                      fill="#3b82f6" 
                      name="Allocations"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 bg-slate-50 rounded-lg">
                  <p className="text-slate-500">No allocation data available for the selected date range</p>
                </div>
              )}
            </Card>

            {/* Additional Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Key Metrics vs Benchmark */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Key Metrics vs Benchmark</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { name: 'Total Allocations', value: analytics.totalAllocations, benchmark: benchmarks?.totalAllocations },
                      { name: 'Total Calls', value: analytics.totalCalls, benchmark: benchmarks?.totalCalls },
                      { name: 'Proactive', value: analytics.proactiveCheckins, benchmark: benchmarks?.proactiveCheckins },
                      { name: 'On Demand', value: analytics.onDemandCheckins, benchmark: benchmarks?.onDemandCheckins },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" name="Actual" fill="#3b82f6" />
                    <Bar dataKey="benchmark" name="Benchmark" fill="#94a3b8" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              {/* Proactive vs On Demand Pie Chart */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Call Type Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Proactive Checkins', value: analytics.proactiveCheckins || 0 },
                        { name: 'On Demand Calls', value: analytics.onDemandCheckins || 0 },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell fill="#22c55e" />
                      <Cell fill="#f59e0b" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              {/* Average Metrics vs Benchmark */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Average Metrics vs Benchmark</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { name: 'Calls/Alloc', value: analytics.avgCallsPerAllocation, benchmark: benchmarks?.avgCallsPerAllocation },
                      { name: 'Proactive/Alloc', value: analytics.avgProactivePerAllocation, benchmark: benchmarks?.avgProactivePerAllocation },
                      { name: 'OnDemand/Alloc', value: analytics.avgOnDemandPerAllocation, benchmark: benchmarks?.avgOnDemandPerAllocation },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" name="Actual" fill="#8b5cf6" />
                    <Bar dataKey="benchmark" name="Benchmark" fill="#94a3b8" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              {/* Time Metrics */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Time Metrics (seconds)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { name: 'Avg Time Per Call', value: analytics.avgTimePerCall, benchmark: benchmarks?.avgTimePerCall },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" name="Actual" fill="#06b6d4" />
                    <Bar dataKey="benchmark" name="Benchmark" fill="#94a3b8" />
                    {benchmarks?.avgTimePerCall && <ReferenceLine y={benchmarks.avgTimePerCall} stroke="#ef4444" strokeDasharray="5 5" />}
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          </>
        )}
      </div>
    </UserLayout>
  );
};
